import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { IconButton } from 'shared/ui/IconButton'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import { useWidgetKeyboard } from 'pages/chatbot/ui/UnifiedAgent/hooks/useWidgetKeyboard'
import { getLastStepHintMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/getLastStepHintMessage'
import {
  WidgetCard,
  WidgetFooter,
  WidgetHint,
} from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import {
  AutoFollowUpWidgetStore,
  STEP_HEADERS,
} from 'pages/chatbot/ui/UnifiedAgent/ui/AutoFollowUpSection/store/autoFollowUpWidgetStore'
import { AutoFollowUpStep1 } from 'pages/chatbot/ui/UnifiedAgent/ui/AutoFollowUpSection/ui/Steps/AutoFollowUpStep1'
import { AutoFollowUpStep2Window } from 'pages/chatbot/ui/UnifiedAgent/ui/AutoFollowUpSection/ui/Steps/AutoFollowUpStep2Window'
import { AutoFollowUpStep3 } from 'pages/chatbot/ui/UnifiedAgent/ui/AutoFollowUpSection/ui/Steps/AutoFollowUpStep3'
import { AutoFollowUpStep2EndSession } from 'pages/chatbot/ui/UnifiedAgent/ui/AutoFollowUpSection/ui/Steps/AutoFollowUpStep2EndSession'

const DEFAULT_HINT = ['↑↓ to navigate', 'Enter to select']

export const AutoFollowUpWidget = observer(() => {
  const { autoFollowUpSectionStore, chatStore } = useUnifiedAgentContext()
  const [store] = useState(() => new AutoFollowUpWidgetStore(autoFollowUpSectionStore, chatStore))

  useEffect(() => {
    return () => {
      store.reset()
    }
  }, [])

  useWidgetKeyboard({
    onNavigate: (dir) => store.moveFocus(dir),
    onEnter: store.handleSelect,
    onSubmit: store.handleSubmit,
    onEscape: store.handleClose,
    isLastStep: store.isLastStep,
  })

  const hintItems = store.isLastStep
    ? getLastStepHintMessage(['↑↓ to navigate'])
    : [...DEFAULT_HINT, 'Esc to skip']

  const getHeader = () => {
    if (store.step === 1) return STEP_HEADERS.shouldFollowUp
    if (store.step === 2) {
      return store.isFollowUp ? STEP_HEADERS.followUpWindow : STEP_HEADERS.sessionLifetime
    }
    return STEP_HEADERS.assertiveness
  }

  const renderContent = () => {
    switch (store.step) {
      case 1:
        return <AutoFollowUpStep1 focusedIndex={store.safeIndex} onSelect={store.selectAt} />
      case 2:
        return store.isFollowUp ? (
          <AutoFollowUpStep2Window focusedIndex={store.safeIndex} onSelect={store.selectAt} />
        ) : (
          <AutoFollowUpStep2EndSession
            focusedIndex={store.safeIndex}
            onSelect={store.setFocusedIndex}
          />
        )
      case 3:
        return <AutoFollowUpStep3 focusedIndex={store.safeIndex} onSelect={store.setFocusedIndex} />
      default:
        return null
    }
  }

  return (
    <>
      <WidgetCard header={getHeader()}>
        {renderContent()}
        <WidgetFooter
          step={store.step}
          total={store.totalSteps}
          onSkip={store.handleClose}
          onBack={store.step > 1 ? store.goBack : undefined}
          action={
            store.isLastStep && (
              <IconButton
                icon={'arrowUp'}
                ariaLabel={'WidgetSubmit'}
                color={'primary'}
                onClick={store.handleSubmit}
              />
            )
          }
        />
      </WidgetCard>
      <WidgetHint items={hintItems} />
    </>
  )
})
